import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

import { useSelector } from 'react-redux';

import { StreamTypes } from 'store/player/actions.types.ts';
import { PlayerTrack, PlayerTracks } from 'store/player/reducer.types';
import {
    selectPlayerAudio,
    selectPlayerSubtitles,
} from 'store/player/selectors';
import {
    OFF_OPTION,
    PLAYER_TRACK_LANGUAGE_TO_LANGUAGE_OPTIONS,
} from 'store/player/utils';

import { isAudioAndSubtitles } from 'screens/PlayerModal/utils';

import { PlayerRef } from 'components/Player/types.ts';

type Props = {
    playerRef: RefObject<PlayerRef | null>;
    visible: boolean;
    readyToPlay: boolean;
    channelId: string | undefined;
    streamType: StreamTypes | undefined;
};

const INITIAL_TRACKS_STATE = {
    audioTracks: [],
    textTracks: [],
};

function useAudioAndSubtitles({
    playerRef,
    visible,
    readyToPlay,
    channelId,
    streamType,
}: Props) {
    const defaultSubtitles = useSelector(selectPlayerSubtitles);
    const defaultAudio = useSelector(selectPlayerAudio);

    const prevChannelId = useRef(channelId);
    const prevStreamType = useRef(streamType);

    const [playerTracks, setPlayerTracks] =
        useState<PlayerTracks>(INITIAL_TRACKS_STATE);
    const [selectedAudio, setSelectedAudio] = useState<
        undefined | PlayerTrack
    >();
    const [selectedSubtitles, setSelectedSubtitles] = useState<
        undefined | PlayerTrack
    >();

    const handleSelectAudio = useCallback((track: PlayerTrack) => {
        setSelectedAudio(track);

        playerRef.current?.selectAudioTrack(track.id);
    }, []);

    const handleSelectSubtitles = useCallback((track: PlayerTrack) => {
        setSelectedSubtitles(track);

        playerRef.current?.selectTextTrack(track.id);
    }, []);

    useEffect(() => {
        if (readyToPlay) {
            // When user is pausing live stream - only stream type changes.
            // For all other cases - readyToPlay flag would be changed.
            const channelStreamTypeChanged =
                prevChannelId.current === channelId &&
                prevStreamType.current !== streamType;

            if (isAudioAndSubtitles(playerTracks)) {
                let audio: PlayerTrack | undefined;
                let subtitles: PlayerTrack | undefined;

                if (channelStreamTypeChanged) {
                    if (selectedAudio?.language) {
                        audio = playerTracks.audioTracks.find(
                            ({ language }) =>
                                selectedAudio?.language === language,
                        );
                    }

                    if (selectedSubtitles?.language) {
                        subtitles = playerTracks.textTracks.find(
                            ({ language }) =>
                                selectedSubtitles?.language === language,
                        );
                    }
                } else {
                    audio = playerTracks.audioTracks.find(({ language }) =>
                        PLAYER_TRACK_LANGUAGE_TO_LANGUAGE_OPTIONS[
                            defaultAudio
                        ]?.includes(language),
                    );
                    subtitles = playerTracks.textTracks.find(({ language }) =>
                        PLAYER_TRACK_LANGUAGE_TO_LANGUAGE_OPTIONS[
                            defaultSubtitles
                        ]?.includes(language),
                    );
                }

                if (!audio) {
                    [audio] = playerTracks.audioTracks;
                }

                if (!subtitles) {
                    subtitles = OFF_OPTION;
                }

                handleSelectAudio(audio);
                handleSelectSubtitles(subtitles);
            } else {
                setSelectedAudio(undefined);
                setSelectedSubtitles(undefined);
                setPlayerTracks(INITIAL_TRACKS_STATE);
            }
        }

        prevChannelId.current = channelId;
        prevStreamType.current = streamType;
    }, [readyToPlay]);

    const handleTracksChanged = useCallback((tracks: PlayerTracks) => {
        const tracksList = {
            ...tracks,
            textTracks: [OFF_OPTION, ...tracks.textTracks],
        };

        if (isAudioAndSubtitles(tracksList)) {
            setPlayerTracks(tracksList);
        } else {
            setPlayerTracks(INITIAL_TRACKS_STATE);
        }
    }, []);

    useEffect(() => {
        if (!visible) {
            setSelectedSubtitles(OFF_OPTION);
            setSelectedAudio(undefined);
            setPlayerTracks(INITIAL_TRACKS_STATE);

            prevChannelId.current = undefined;
            prevStreamType.current = undefined;
        }
    }, [visible]);

    return {
        playerTracks,
        selectedAudio,
        selectedSubtitles,
        onTracksChanged: handleTracksChanged,
        onSelectAudio: handleSelectAudio,
        onSelectSubtitles: handleSelectSubtitles,
    };
}

export default useAudioAndSubtitles;
